import { Text } from "@imminently/react/ui/core/text";
import { Box, IconButton, Stack } from "@mui/material";
import { GridColDef, GridEventListener } from "@mui/x-data-grid";
import { DataGrid, GridRowClassNameParams } from "@mui/x-data-grid";

import { Icon } from "@/components/Icon";
import RiskScore from "@/components/RiskScore";
import { FullNameCell, getFullNameValue } from "@/components/TableCells";
import { formatDuration, getTimeFromNow } from "@/global";
import { PatientEncounter } from "@/resources/types";
import { css, Theme } from "@/theme";
import { useNavigate } from "react-router";
import { useCallback } from "react";
import { getPatientRoute } from "@/pages";
import { TabNames } from "@/pages/patient/patient";
import { createSearchParams } from "react-router-dom";

const styles = {
  table: (theme: Theme) => css`
    border: none;

    .MuiDataGrid-columnHeaders {
      border: none;

      .MuiDataGrid-columnSeparator svg {
        opacity: 0;
      }
    }

    .MuiDataGrid-virtualScroller {
      overflow: hidden;
      border: 1px solid ${theme.palette.primary[100]};
      border-radius: ${theme.spacing(2)};

      .observation-row--even {
        background-color: ${theme.palette.primary[25]};
      }
    }

    .MuiDataGrid-row{
      cursor: pointer;
    }

    .MuiDataGrid-row{
      cursor: pointer;
    }

    .observation-column--due {
      padding-left: 20px;
    }

    .observation-column--risk {
      padding-right: 10px;
    }
  `,
};

export const RiskScoreCell = ({ row }: { row: PatientEncounter }) => {
  const { riskScore } = row;
  return riskScore ? <RiskScore riskScore={riskScore} /> : null;
};

export const MinsDueCell = ({ row }: { row: any }) => {
  const isOverdue = row.isOverdue;
  return (
    <Text
      variant="body2"
      color={isOverdue ? "error.main" : undefined}
    >
      {row.minsDue}
    </Text>
  );
};

const columns: GridColDef[] = [
  {
    field: "minsDue",
    headerName: "Due",
    width: 100,
    cellClassName: "observation-column--due",
    headerClassName: "observation-column--due",
    renderCell: MinsDueCell,
  },
  {
    field: "name",
    headerName: "Patient Name",
    flex: 1,
    valueGetter: getFullNameValue,
    renderCell: FullNameCell,
  },
  {
    field: "riskScore",
    headerName: "Risk Score",
    width: 110,
    cellClassName: "observation-column--risk",
    headerClassName: "observation-column--risk",
    valueGetter: (_, row) => row.riskScore && row.riskScore.score,
    renderCell: RiskScoreCell,
  },
] as GridColDef[];

const ObservationTable = (props: { observations: PatientEncounter[] }) => {
  const getRowClass = (params: GridRowClassNameParams) => {
    const evenRow = params.indexRelativeToCurrentPage % 2 === 0;
    return evenRow ? "observation-row--even" : "";
  };
  const navigate = useNavigate();


  const rows = props.observations
    .filter((o) => o.nextObsDue !== undefined)
    .map((observation) => {
      const duration = getTimeFromNow(observation.nextObsDue as number);
      const isOverdue = duration.asMinutes() <= 0;

      return {
        ...observation,
        minsDue: formatDuration(duration),
        isOverdue,
        id: observation.patient.id,
        encounterId: observation.id,
      };
    });

  const redirectToPatientTable: GridEventListener<"rowClick"> = useCallback(({ row }) => {

    navigate({
      pathname: getPatientRoute(String(row.encounterId)),
      search: createSearchParams({ tab: TabNames.OBS_CHART }).toString()
    });
  }, [navigate, getPatientRoute]);

  return (
    <DataGrid
      css={styles.table}
      autoHeight
      disableColumnMenu
      hideFooter
      rows={rows}
      columns={columns}
      getRowClassName={getRowClass}
      onRowClick={redirectToPatientTable}
    />
  );
};

type NextObservationsPanelProps = {
  observations: PatientEncounter[];
  filteredAreas?: string[];
  onClose: () => void;
};

const NextObservationsPanel = (props: NextObservationsPanelProps) => (
  <Stack px={5} py={3} height="100%" gap={4} bgcolor="#F0F3FF">
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Text variant="h4">Next observations due</Text>
      <IconButton onClick={props.onClose}>
        <Icon icon="close" />
      </IconButton>
    </Stack>

    {props.filteredAreas && props.filteredAreas.length > 0 && (
      <Text variant="body2" color="text.secondary">
        {`Location${props.filteredAreas.length > 1 ? "s" : ""}: ${props.filteredAreas.join(", ")}`}
      </Text>
    )}

    <Box width="500px">
      <ObservationTable observations={props.observations} />
    </Box>
  </Stack>
);

export default NextObservationsPanel;
